// src/components/GlobalOverview.js
import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { PHASES, PHASE_COLORS, platShort, platColor } from '../constants';

const PLAT_NAMES = ['Google Ads', 'Microsoft', 'LSA', 'Meta', 'Yelp', 'Nextdoor'];

function alertLevel(c) {
  if (c.onHold || c.phase === 'postlaunch' || c.phase === 'cancelled') return '';
  const d = c.lastChecked ? Math.floor((new Date() - new Date(c.lastChecked)) / 86400000) : 999;
  if (d >= 7) return 'urgent';
  if (d >= 4) return 'stale';
  return '';
}

function phaseBadge(phase) {
  const ph = PHASES.find(p => p.id === phase);
  const c = PHASE_COLORS[phase] || { bg: '#eee', text: '#555' };
  return { label: ph ? ph.label : phase, ...c };
}

function fmt(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date() - new Date(dateStr + 'T00:00:00')) / 86400000);
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.floor((new Date(dateStr + 'T00:00:00') - new Date()) / 86400000);
}

export default function GlobalOverview({ allUsers }) {
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(true);

  // View / filter state
  const [activeTab, setActiveTab] = useState('onboarding'); // onboarding | launched | all
  const [filterPhase, setFilterPhase] = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterOwner, setFilterOwner] = useState('');
  const [filterVertical, setFilterVertical] = useState('');
  const [launchWindow, setLaunchWindow] = useState('30'); // preset
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('phase'); // phase | owner | platform

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'clients'), snap => {
      // Exclude PM-only accounts from global overview
      setAllClients(snap.docs.map(d => ({ ...d.data(), id: d.id })).filter(c => !c.opspm));
      setLoading(false);
    });
    return unsub;
  }, []);

  function ownerName(uid) {
    const u = allUsers.find(u => u.uid === uid);
    return u ? (u.displayName || u.email) : uid;
  }

  // All launched platforms across all clients
  const launchedPlatforms = [];
  allClients.forEach(c => {
    (c.platforms || []).forEach(p => {
      if (p.launched && p.launchDate) {
        launchedPlatforms.push({ client: c, platform: p });
      }
    });
  });

  // Filter helpers
  const windowDays = parseInt(launchWindow) || 30;

  // Date range for launches: custom overrides preset
  const isCustomRange = launchWindow === 'custom' && (customFrom || customTo);
  const recentLaunches = launchedPlatforms
    .filter(({ platform: p }) => {
      if (!p.launchDate) return false;
      const ld = new Date(p.launchDate + 'T00:00:00');
      if (isCustomRange) {
        const from = customFrom ? new Date(customFrom + 'T00:00:00') : new Date('2000-01-01');
        const to = customTo ? new Date(customTo + 'T23:59:59') : new Date();
        return ld >= from && ld <= to;
      }
      const d = daysSince(p.launchDate);
      return d !== null && d >= 0 && d <= windowDays;
    })
    .filter(({ client: c }) => !filterOwner || c.ownerUid === filterOwner)
    .filter(({ platform: p }) => !filterPlatform || p.name === filterPlatform)
    .sort((a, b) => new Date(b.platform.launchDate) - new Date(a.platform.launchDate));

  const upcomingLaunches = allClients
    .filter(c => c.projLaunch && daysUntil(c.projLaunch) !== null && daysUntil(c.projLaunch) >= 0 && daysUntil(c.projLaunch) <= 30)
    .filter(c => c.phase !== 'postlaunch')
    .filter(c => !filterOwner || c.ownerUid === filterOwner)
    .sort((a, b) => new Date(a.projLaunch) - new Date(b.projLaunch));

  const verticals = [...new Set(allClients.flatMap(c => Array.isArray(c.verticals) && c.verticals.length ? c.verticals : (c.vertical ? [c.vertical] : [])).filter(Boolean))].sort();

  const onboardingClients = allClients
    .filter(c => c.phase !== 'postlaunch' && c.phase !== 'cancelled')
    .filter(c => !c.onHold || filterPhase === 'all')
    .filter(c => !filterPhase || c.phase === filterPhase)
    .filter(c => !filterPlatform || (c.platforms || []).some(p => p.name === filterPlatform))
    .filter(c => !filterOwner || c.ownerUid === filterOwner)
    .filter(c => !filterVertical || (Array.isArray(c.verticals) ? c.verticals.includes(filterVertical) : c.vertical === filterVertical))
    .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const phaseOrder = PHASES.map(p => p.id);
      return phaseOrder.indexOf(a.phase) - phaseOrder.indexOf(b.phase);
    });

  const allFilteredClients = allClients
    .filter(c => !filterPhase || c.phase === filterPhase)
    .filter(c => !filterPlatform || (c.platforms || []).some(p => p.name === filterPlatform))
    .filter(c => !filterOwner || c.ownerUid === filterOwner)
    .filter(c => !filterVertical || (Array.isArray(c.verticals) ? c.verticals.includes(filterVertical) : c.vertical === filterVertical))
    .filter(c => !search || c.name?.toLowerCase().includes(search.toLowerCase()));

  // Stats
  const totalOnboarding = allClients.filter(c => c.phase !== 'postlaunch' && !c.onHold).length;
  const onHoldCount = allClients.filter(c => c.onHold).length;
  const postlaunchCount = allClients.filter(c => c.phase === 'postlaunch').length;
  const urgentCount = allClients.filter(c => alertLevel(c) === 'urgent').length;
  const launchedThisMonth = launchedPlatforms.filter(({ platform: p }) => daysSince(p.launchDate) <= 30).length;
  const cancelledCount = allClients.filter(c => c.phase === 'cancelled').length;
  const totalActiveBudget = allClients
    .filter(c => !c.onHold && c.phase !== 'postlaunch')
    .reduce((s, c) => s + (c.platforms || []).reduce((ps, p) => ps + (parseFloat(p.budget) || 0), 0), 0);

  // MRR calculations
  const totalMRR = allClients.reduce((s, c) => s + (parseFloat(c.mrrAmount) || 0), 0);
  const onboardingMRR = allClients
    .filter(c => !c.onHold && c.phase !== 'postlaunch')
    .reduce((s, c) => s + (parseFloat(c.mrrAmount) || 0), 0);
  const launchedMRR = allClients
    .filter(c => c.phase === 'postlaunch')
    .reduce((s, c) => s + (parseFloat(c.mrrAmount) || 0), 0);
  const holdMRR = allClients
    .filter(c => c.onHold)
    .reduce((s, c) => s + (parseFloat(c.mrrAmount) || 0), 0);

  // Grouping for onboarding view
  function groupClients(clients) {
    if (groupBy === 'phase') {
      return PHASES.filter(ph => ph.id !== 'postlaunch').map(ph => ({
        key: ph.id,
        label: ph.label,
        color: ph.color,
        clients: clients.filter(c => c.phase === ph.id),
      })).filter(g => g.clients.length > 0);
    }
    if (groupBy === 'owner') {
      const owners = [...new Set(clients.map(c => c.ownerUid))];
      return owners.map(uid => ({
        key: uid,
        label: ownerName(uid),
        color: '#185FA5',
        clients: clients.filter(c => c.ownerUid === uid),
      }));
    }
    if (groupBy === 'platform') {
      return PLAT_NAMES.map(name => ({
        key: name,
        label: name,
        color: platColor(name).text,
        clients: clients.filter(c => (c.platforms || []).some(p => p.name === name)),
      })).filter(g => g.clients.length > 0);
    }
    return [];
  }


  function exportToCSV() {
    const phase = (id) => PHASES.find(p => p.id === id)?.label || id;
    const vertStr = (c) => Array.isArray(c.verticals) && c.verticals.length ? c.verticals.join('; ') : (c.vertical || '');
    const platStr = (c) => (c.platforms || []).map(p => {
      const budget = p.budget ? `$${Number(p.budget).toLocaleString()}` : '';
      const fee = p.feePercent ? `${p.feePercent}%` : '';
      const launched = p.launched ? `Launched${p.launchDate ? ' ' + p.launchDate : ''}` : '';
      return [p.name, p.accountType === 'new' ? 'New' : 'Existing', budget, fee, launched].filter(Boolean).join(' - ');
    }).join(' | ');
    const mrr = (c) => c.mrrAmount ? `$${Number(c.mrrAmount).toLocaleString()}` : '';
    const totalBudget = (c) => {
      const t = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
      return t > 0 ? `$${t.toLocaleString()}` : '';
    };
    const lpProgress = (c) => {
      const LP_STEPS_LOCAL = ['Fill out LP Build Template','Order Sanity Project','Order DES Assets','Request Landing Pages be created','Landing Page Review','Landing Pages Approved'];
      const done = LP_STEPS_LOCAL.filter(s => c.lpSteps?.[s]).length;
      return `${done}/${LP_STEPS_LOCAL.length}`;
    };
    const trkProgress = (c) => {
      const TRK = ['Gather tracking deliverables','Fill out Tracking Table','Tag tracking team','Tracking ordered/started','Paid Media Tracking setup','Tracking Q/A complete'];
      const done = TRK.filter(s => c.trackSteps?.[s]).length;
      return `${done}/${TRK.length}`;
    };
    const pmProgress = (c) => {
      const PM = ['New Client Email','Post-Sales Call','Paid Media Survey','Website Survey','Paid Media Analysis','Kick-off Call','Critical for Launch Surveys','Follow Up Strategy Call','Internal Pre-Launch Call','Pre-Launch Client Call','Launch Scheduled','Launch Email Sent'];
      const done = PM.filter(s => c.pmSteps?.[s]).length;
      return `${done}/${PM.length}`;
    };

    // Determine which clients to export based on active tab + filters
    let exportClients = [];
    if (activeTab === 'onboarding') exportClients = onboardingClients;
    else if (activeTab === 'all') exportClients = allFilteredClients;
    else if (activeTab === 'launched') {
      exportClients = [...new Set(recentLaunches.map(r => r.client.id))].map(id => allClients.find(c => c.id === id)).filter(Boolean);
    }

    const headers = [
      'Client Name', 'CID/ID', 'Phase', 'On Hold', 'Platforms', 'Ads Destination',
      'Vertical(s)', 'Total Budget/mo', 'Paid Media MRR', 'CSM/PM', 'Strategist',
      'Account POC', 'POC Email', 'Kick-Off Date', 'Proj. Launch', 'Actual Launch',
      'Last Checked', 'LP Progress', 'Tracking Progress', 'PM Progress',
      'Notes', 'Pinned Note', 'Owner'
    ];

    const rows = exportClients.map(c => [
      c.name || '',
      c.cids || '',
      phase(c.phase),
      c.onHold ? 'Yes' : 'No',
      platStr(c),
      c.adsDestination || '',
      vertStr(c),
      totalBudget(c),
      mrr(c),
      c.csm || '',
      c.strategist || '',
      c.accountPoc || '',
      c.pocEmail || '',
      c.kickoff || '',
      c.projLaunch || '',
      c.actualLaunch || '',
      c.lastChecked || '',
      lpProgress(c),
      trkProgress(c),
      pmProgress(c),
      (c.notes || '').replace(/"/g, '""'),
      (c.pinNote || '').replace(/"/g, '""'),
      ownerName(c.ownerUid),
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const tabLabel = activeTab === 'onboarding' ? 'Onboarding' : activeTab === 'launched' ? 'Launches' : 'All-Accounts';
    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `AdFlowz-${tabLabel}-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  const selStyle = {
    fontSize: 12, padding: '5px 8px', border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: 7, background: '#fff', color: '#1a1a18', fontFamily: 'inherit',
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: 13 }}>
      Loading all accounts…
    </div>
  );

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#f5f5f3' }}>

      {/* Top stats bar */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '10px 16px', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
        flexShrink: 0,
      }}>
        {[
          { label: 'In Onboarding', value: totalOnboarding, color: '#185FA5', bg: '#E6F1FB', tab: 'onboarding' },
          { label: 'Launched (30d)', value: launchedThisMonth, color: '#0F6E56', bg: '#dcf0e8', tab: 'launched' },
          { label: '🚀 Launched', value: postlaunchCount, color: '#0F6E56', bg: '#dcf0e8', tab: 'all' },
          { label: '✕ Cancelled', value: cancelledCount, color: '#666664', bg: '#f0f0ee', tab: 'all' },
          { label: 'On Hold', value: onHoldCount, color: '#A32D2D', bg: '#FCEBEB', tab: null },
          { label: '⚠ Urgent', value: urgentCount, color: '#A32D2D', bg: '#FCEBEB', tab: 'onboarding' },
        ].map(s => (
          <div
            key={s.label}
            onClick={() => s.tab && setActiveTab(s.tab)}
            style={{
              background: s.bg, borderRadius: 7, padding: '4px 12px',
              display: 'flex', alignItems: 'center', gap: 7,
              cursor: s.tab ? 'pointer' : 'default',
              border: activeTab === s.tab ? `2px solid ${s.color}` : '2px solid transparent',
              transition: 'border 0.15s',
            }}
            onMouseEnter={e => { if (s.tab) e.currentTarget.style.border = `2px solid ${s.color}88`; }}
            onMouseLeave={e => { e.currentTarget.style.border = activeTab === s.tab ? `2px solid ${s.color}` : '2px solid transparent'; }}
          >
            <span style={{ fontSize: 18, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.value}</span>
            <span style={{ fontSize: 11, color: s.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{s.label}</span>
          </div>
        ))}
        {totalActiveBudget > 0 && (
          <div style={{
            background: '#E6F1FB', borderRadius: 7, padding: '4px 12px',
            display: 'flex', alignItems: 'center', gap: 7,
            border: '2px solid transparent',
          }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: '#185FA5', lineHeight: 1 }}>
              ${Math.round(totalActiveBudget / 1000)}k
            </span>
            <span style={{ fontSize: 11, color: '#185FA5', fontWeight: 600, whiteSpace: 'nowrap' }}>Active Spend/mo</span>
          </div>
        )}
        {totalMRR > 0 && (
          <div style={{
            background: '#EAF3DE', borderRadius: 7, padding: '4px 12px',
            display: 'flex', alignItems: 'center', gap: 7,
            border: '2px solid transparent',
          }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: '#0F6E56', lineHeight: 1 }}>
              ${Math.round(totalMRR / 1000)}k
            </span>
            <span style={{ fontSize: 11, color: '#0F6E56', fontWeight: 600, whiteSpace: 'nowrap' }}>Total MRR</span>
          </div>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search clients…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ ...selStyle, width: 160 }}
          />
          <button
            onClick={exportToCSV}
            title="Download current view as spreadsheet"
            style={{
              fontSize: 12, padding: '5px 12px',
              background: '#185FA5', color: '#fff',
              border: 'none', borderRadius: 7,
              cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700,
              whiteSpace: 'nowrap',
            }}
          >
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Tab bar + filters */}
      <div style={{
        background: '#fff', borderBottom: '1px solid rgba(0,0,0,0.1)',
        padding: '0 16px', display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0,
      }}>
        {[
          { id: 'onboarding', label: '🚦 In Onboarding' },
          { id: 'launched', label: '🚀 Launches' },
          { id: 'all', label: '☰ All Accounts' },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            fontSize: 12, padding: '10px 16px', background: 'none', border: 'none',
            borderBottom: activeTab === t.id ? '2px solid #185FA5' : '2px solid transparent',
            color: activeTab === t.id ? '#185FA5' : '#666',
            fontWeight: activeTab === t.id ? 700 : 400,
            cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
            {t.label}
          </button>
        ))}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, padding: '6px 0', flexWrap: 'wrap' }}>
          {activeTab === 'onboarding' && (
            <>
              <select value={groupBy} onChange={e => setGroupBy(e.target.value)} style={selStyle}>
                <option value="phase">Group by Phase</option>
                <option value="owner">Group by Owner</option>
                <option value="platform">Group by Platform</option>
              </select>
              <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={selStyle}>
                <option value="">All phases</option>
                {PHASES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
              </select>
            </>
          )}
          {activeTab === 'launched' && (
            <>
              <select value={launchWindow} onChange={e => setLaunchWindow(e.target.value)} style={selStyle}>
                <option value="7">Last 7 days</option>
                <option value="14">Last 14 days</option>
                <option value="30">Last 30 days</option>
                <option value="60">Last 60 days</option>
                <option value="90">Last 90 days</option>
                <option value="180">Last 6 months</option>
                <option value="365">Last year</option>
                <option value="custom">Custom range…</option>
              </select>
              {launchWindow === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={e => setCustomFrom(e.target.value)}
                    style={{ ...selStyle, fontSize: 11 }}
                    title="From date"
                  />
                  <span style={{ fontSize: 11, color: '#888', alignSelf: 'center' }}>→</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={e => setCustomTo(e.target.value)}
                    style={{ ...selStyle, fontSize: 11 }}
                    title="To date"
                  />
                </>
              )}
            </>
          )}
          <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} style={selStyle}>
            <option value="">All platforms</option>
            {PLAT_NAMES.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <select value={filterOwner} onChange={e => setFilterOwner(e.target.value)} style={selStyle}>
            <option value="">All team members</option>
            {allUsers.map(u => <option key={u.uid} value={u.uid}>{u.displayName || u.email}</option>)}
          </select>
          {activeTab !== 'launched' && (
            <select value={filterVertical} onChange={e => setFilterVertical(e.target.value)} style={selStyle}>
              <option value="">All verticals</option>
              {verticals.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

        {/* ONBOARDING TAB */}
        {activeTab === 'onboarding' && (
          <div>
            {totalMRR > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, padding: '10px 14px', background: '#fff', borderRadius: 10, border: '1px solid rgba(0,0,0,0.08)' }}>
                <MRRPill label="Total MRR" value={totalMRR} color="#0F6E56" bg="#EAF3DE" />
                <MRRPill label="In Onboarding" value={onboardingMRR} color="#185FA5" bg="#E6F1FB" />
                <MRRPill label="🚀 Launched" value={launchedMRR} color="#0F6E56" bg="#dcf0e8" />
                {holdMRR > 0 && <MRRPill label="On Hold" value={holdMRR} color="#A32D2D" bg="#FCEBEB" />}
                {(filterPhase || filterPlatform || filterOwner || filterVertical) && onboardingClients.length > 0 && (
                  <MRRPill label="Filtered MRR" value={onboardingClients.reduce((s, c) => s + (parseFloat(c.mrrAmount) || 0), 0)} color="#854F0B" bg="#FAEEDA" />
                )}
              </div>
            )}
            {onboardingClients.length === 0
              ? <p style={{ textAlign: 'center', padding: 40, color: '#aaa', fontSize: 13 }}>No clients match your filters</p>
              : groupBy === 'phase'
                ? groupClients(onboardingClients).map(group => <PhaseGroup key={group.key} group={group} ownerName={ownerName} />)
                : groupBy === 'owner'
                  ? groupClients(onboardingClients).map(group => <OwnerGroup key={group.key} group={group} />)
                  : groupClients(onboardingClients).map(group => <PlatformGroup key={group.key} group={group} ownerName={ownerName} />)
            }
          </div>
        )}

        {/* LAUNCHED TAB */}
        {activeTab === 'launched' && (
          <div>
            {/* Upcoming launches */}
            {upcomingLaunches.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <SectionHeader label={`📅 Projected to Launch in Next 30 Days (${upcomingLaunches.length})`} color="#BA7517" />
                <table style={tableStyle}>
                  <thead>
                    <tr>{['Client','Platforms','Owner','Proj. Launch','Days Away','CSM'].map(h => <Th key={h}>{h}</Th>)}</tr>
                  </thead>
                  <tbody>
                    {upcomingLaunches.map(c => (
                      <tr key={c.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        <Td><strong>{c.name}</strong>{(Array.isArray(c.verticals) && c.verticals.length ? c.verticals : (c.vertical ? [c.vertical] : [])).map((v,i) => <span key={i} style={{ marginLeft: 4, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: '#f0f0f0', color: '#666' }}>{v}</span>)}</Td>
                        <Td><PlatTags platforms={c.platforms} /></Td>
                        <Td style={{ color: '#888' }}>{ownerName(c.ownerUid)}</Td>
                        <Td style={{ fontWeight: 700, color: '#BA7517' }}>{fmt(c.projLaunch)}</Td>
                        <Td>
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: '#FAEEDA', color: '#854F0B' }}>
                            {daysUntil(c.projLaunch)}d away
                          </span>
                        </Td>
                        <Td style={{ color: '#888' }}>{c.csm || '—'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent launches */}
            <SectionHeader
              label={`🚀 ${isCustomRange
                ? `Launched ${customFrom ? customFrom : 'start'} → ${customTo ? customTo : 'today'}`
                : `Launched in Last ${launchWindow} Days`} (${recentLaunches.length} platforms)`}
              color="#0F6E56"
            />
            {recentLaunches.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: '#aaa', fontSize: 13 }}>
                No platforms launched in this window
              </div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>{['Client','Platform','Launch Date','Days Ago','Owner','CSM','Vertical','Budget'].map(h => <Th key={h}>{h}</Th>)}</tr>
                </thead>
                <tbody>
                  {recentLaunches.map(({ client: c, platform: p }, i) => {
                    const d = daysSince(p.launchDate);
                    const col = platColor(p.name);
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                        <Td>
                          <strong>{c.name}</strong>
                          {c.pinNote && <div style={{ fontSize: 11, color: '#854F0B' }}>📌 {c.pinNote}</div>}
                        </Td>
                        <Td>
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: col.bg, color: col.text }}>
                            {p.name}
                          </span>
                        </Td>
                        <Td style={{ fontWeight: 700, color: '#0F6E56' }}>{fmt(p.launchDate)}</Td>
                        <Td>
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, fontWeight: 700, background: '#dcf0e8', color: '#0F6E56' }}>
                            {d === 0 ? 'Today' : `${d}d ago`}
                          </span>
                        </Td>
                        <Td style={{ color: '#888' }}>{ownerName(c.ownerUid)}</Td>
                        <Td style={{ color: '#888' }}>{c.csm || '—'}</Td>
                        <Td style={{ color: '#888' }}>{Array.isArray(c.verticals) && c.verticals.length ? c.verticals.join(', ') : (c.vertical || '—')}</Td>
                        <Td style={{ color: '#888' }}>{p.budget ? `$${Number(p.budget).toLocaleString()}` : '—'}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* ALL ACCOUNTS TAB */}
        {activeTab === 'all' && (
          <div>
            <SectionHeader label={`All Accounts (${allFilteredClients.length})`} color="#555" />
            <table style={tableStyle}>
              <thead>
                <tr>{['Client','CID/ID','Phase','Platforms','Owner','CSM','Proj. Launch','Last Checked','Status'].map(h => <Th key={h}>{h}</Th>)}</tr>
              </thead>
              <tbody>
                {allFilteredClients
                  .sort((a, b) => {
                    const po = PHASES.map(p => p.id);
                    return po.indexOf(a.phase) - po.indexOf(b.phase);
                  })
                  .map(c => {
                    const al = alertLevel(c);
                    const pb = phaseBadge(c.phase);
                    return (
                      <tr key={c.id} style={{
                        borderBottom: '1px solid rgba(0,0,0,0.05)',
                        borderLeft: al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : undefined,
                        opacity: c.onHold ? 0.6 : 1,
                      }}>
                        <Td>
                          <strong>{c.name}</strong>
                          {c.onHold && <span style={{ marginLeft: 5, fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>Hold</span>}
                          {c.important && <span style={{ marginLeft: 5, fontSize: 10, color: '#185FA5' }}>★</span>}
                          {c.pinNote && <div style={{ fontSize: 11, color: '#854F0B', marginTop: 2 }}>📌 {c.pinNote}</div>}
                        </Td>
                        <Td style={{ fontFamily: 'monospace', fontSize: 11, color: '#888' }}>{c.cids || '—'}</Td>
                        <Td>
                          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: pb.bg, color: pb.text, whiteSpace: 'nowrap' }}>
                            {pb.label}
                          </span>
                        </Td>
                        <Td><PlatTags platforms={c.platforms} /></Td>
                        <Td style={{ color: '#888', fontSize: 11 }}>{ownerName(c.ownerUid)}</Td>
                        <Td style={{ color: '#888', fontSize: 11 }}>{c.csm || '—'}</Td>
                        <Td style={{ fontWeight: 700, color: '#0F6E56', fontSize: 11 }}>{c.mrrAmount > 0 ? `$${Number(c.mrrAmount).toLocaleString()}` : '—'}</Td>
                        <Td style={{ color: '#888', fontSize: 11 }}>{fmt(c.projLaunch) || '—'}</Td>
                        <Td style={{ color: al === 'urgent' ? '#A32D2D' : al === 'stale' ? '#854F0B' : '#888', fontSize: 11, fontWeight: al ? 700 : 400 }}>
                          {c.lastChecked || '—'}
                          {al && <span style={{ marginLeft: 4, fontSize: 10 }}>{al === 'urgent' ? '⚠' : '▲'}</span>}
                        </Td>
                        <Td>
                          {(c.platforms || []).filter(p => p.launched).map((p, i) => (
                            <div key={i} style={{ fontSize: 10, color: '#0F6E56', fontWeight: 700 }}>
                              🚀 {platShort(p.name)}{p.launchDate ? ` ${fmt(p.launchDate)}` : ''}
                            </div>
                          ))}
                        </Td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────

function MRRPill({ label, value, color, bg }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      background: bg, borderRadius: 8, padding: '6px 14px', minWidth: 100,
    }}>
      <span style={{ fontSize: 18, fontWeight: 900, color, lineHeight: 1 }}>
        ${value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toLocaleString()}
      </span>
      <span style={{ fontSize: 10, color, fontWeight: 600, marginTop: 2, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

function SectionHeader({ label, color }) {
  return (
    <div style={{
      fontSize: 12, fontWeight: 800, color, textTransform: 'uppercase',
      letterSpacing: '0.04em', marginBottom: 10, paddingBottom: 6,
      borderBottom: `2px solid ${color}22`,
    }}>
      {label}
    </div>
  );
}

function PlatTags({ platforms }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      {(platforms || []).map((p, i) => {
        const c = platColor(p.name);
        return (
          <span key={i} style={{ fontSize: 10, padding: '1px 5px', borderRadius: 3, fontWeight: 700, background: c.bg, color: c.text }}>
            {platShort(p.name)}
            {p.launched && ' 🚀'}
          </span>
        );
      })}
    </div>
  );
}

function PhaseGroup({ group, ownerName }) {
  const pb = phaseBadge(group.key);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8,
        padding: '7px 12px', borderRadius: 8,
        background: pb.bg, border: `1px solid ${pb.text}22`,
      }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: group.color, flexShrink: 0 }} />
        <span style={{ fontSize: 12, fontWeight: 800, color: pb.text }}>{group.label}</span>
        <span style={{
          fontSize: 11, padding: '1px 7px', borderRadius: 10,
          background: 'rgba(0,0,0,0.08)', color: pb.text, fontWeight: 700,
        }}>{group.clients.length}</span>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>{['Client','Platforms','Owner','CSM','Vertical','Budget','Proj. Launch','Last Checked','Alert'].map(h => <Th key={h}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {group.clients.map(c => {
            const al = alertLevel(c);
            const total = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
            const d = c.lastChecked ? Math.floor((new Date() - new Date(c.lastChecked + 'T00:00:00')) / 86400000) : null;
            return (
              <tr key={c.id} style={{
                borderBottom: '1px solid rgba(0,0,0,0.05)',
                borderLeft: al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : c.important ? '3px solid #185FA5' : undefined,
              }}>
                <Td>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>
                    {c.important && <span style={{ color: '#185FA5', marginRight: 4 }}>★</span>}
                    {c.name}
                  </div>
                  {c.pinNote && <div style={{ fontSize: 11, color: '#854F0B', marginTop: 2 }}>📌 {c.pinNote}</div>}
                  {c.notes && <div style={{ fontSize: 11, color: '#888', marginTop: 2, maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.notes}</div>}
                </Td>
                <Td><PlatTags platforms={c.platforms} /></Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{ownerName(c.ownerUid)}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{c.csm || '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{Array.isArray(c.verticals) && c.verticals.length ? c.verticals.join(', ') : (c.vertical || '—')}</Td>
                <Td style={{ fontWeight: 700, color: '#0F6E56', fontSize: 11 }}>{c.mrrAmount > 0 ? `$${Number(c.mrrAmount).toLocaleString()}` : '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{total > 0 ? `$${total.toLocaleString()}` : '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{fmt(c.projLaunch) || '—'}</Td>
                <Td style={{ color: al === 'urgent' ? '#A32D2D' : al === 'stale' ? '#854F0B' : '#888', fontSize: 11, fontWeight: al ? 700 : 400 }}>
                  {c.lastChecked || '—'}{d !== null ? ` (${d}d)` : ''}
                </Td>
                <Td>
                  {al === 'urgent' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>⚠ URGENT</span>}
                  {al === 'stale' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700, background: '#FAEEDA', color: '#854F0B' }}>▲ Stale</span>}
                  {c.onHold && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>Hold</span>}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OwnerGroup({ group }) {
  const initials = group.label.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div style={{
          width: 28, height: 28, borderRadius: '50%', background: '#185FA5', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 11, fontWeight: 700, flexShrink: 0,
        }}>{initials}</div>
        <span style={{ fontSize: 13, fontWeight: 800 }}>{group.label}</span>
        <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, background: '#E6F1FB', color: '#185FA5', fontWeight: 700 }}>
          {group.clients.length} accounts
        </span>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>{['Client','Phase','Platforms','CSM','Budget','Proj. Launch','Status'].map(h => <Th key={h}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {group.clients.map(c => {
            const al = alertLevel(c);
            const pb = phaseBadge(c.phase);
            const total = (c.platforms || []).reduce((s, p) => s + (parseFloat(p.budget) || 0), 0);
            return (
              <tr key={c.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)', borderLeft: al === 'urgent' ? '3px solid #E24B4A' : al === 'stale' ? '3px solid #EF9F27' : undefined }}>
                <Td><strong>{c.name}</strong>{c.pinNote && <div style={{ fontSize: 11, color: '#854F0B' }}>📌 {c.pinNote}</div>}</Td>
                <Td><span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: pb.bg, color: pb.text, whiteSpace: 'nowrap' }}>{pb.label}</span></Td>
                <Td><PlatTags platforms={c.platforms} /></Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{c.csm || '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{total > 0 ? `$${total.toLocaleString()}` : '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{fmt(c.projLaunch) || '—'}</Td>
                <Td>{al === 'urgent' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700, background: '#FCEBEB', color: '#A32D2D' }}>⚠ Urgent</span>}{al === 'stale' && <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, fontWeight: 700, background: '#FAEEDA', color: '#854F0B' }}>▲ Stale</span>}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PlatformGroup({ group, ownerName }) {
  const c = platColor(group.key);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 5, fontWeight: 800, background: c.bg, color: c.text }}>{group.label}</span>
        <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, background: c.bg, color: c.text, fontWeight: 700 }}>{group.clients.length} accounts</span>
      </div>
      <table style={tableStyle}>
        <thead>
          <tr>{['Client','Phase','Owner','CSM','Budget','Proj. Launch'].map(h => <Th key={h}>{h}</Th>)}</tr>
        </thead>
        <tbody>
          {group.clients.map(client => {
            const pb = phaseBadge(client.phase);
            const p = (client.platforms || []).find(pl => pl.name === group.key);
            return (
              <tr key={client.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                <Td><strong>{client.name}</strong></Td>
                <Td><span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: pb.bg, color: pb.text, whiteSpace: 'nowrap' }}>{pb.label}</span></Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{ownerName(client.ownerUid)}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{client.csm || '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{p?.budget ? `$${Number(p.budget).toLocaleString()}` : '—'}</Td>
                <Td style={{ color: '#888', fontSize: 11 }}>{fmt(client.projLaunch) || '—'}</Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: 12,
  background: '#fff', borderRadius: 10, overflow: 'hidden',
  border: '1px solid rgba(0,0,0,0.08)',
  marginBottom: 4,
};

const Th = ({ children }) => (
  <th style={{
    textAlign: 'left', padding: '7px 12px', fontSize: 10, fontWeight: 700,
    color: '#888', whiteSpace: 'nowrap', background: '#f8f8f8',
    borderBottom: '1px solid rgba(0,0,0,0.08)', textTransform: 'uppercase', letterSpacing: '0.03em',
  }}>{children}</th>
);

const Td = ({ children, style }) => (
  <td style={{ padding: '8px 12px', verticalAlign: 'top', ...style }}>{children}</td>
);
